// Context-aware comms-template recommender for the Comms Hub picker.
// Per TRACON-PRODUCT-VISION-2026-05-20 §V2 fix #2: suggestions are NOT
// a static stage→slug map; they consult the deal's effective-date
// proximity AND last-activity recency so the escalation curve from
// "polite reminder" to "non-renewal notice" happens automatically as
// time passes without the broker re-thinking it on every send.
//
// Imported by:
//   - web-v2/src/components/sheets/DealActionsSheets.jsx (Send action)
//   - web-v2/src/components/account/CommsTimelinePanel.jsx (Compose CTA)
//   - web-v2/src/components/pipeline/PipelineDealCard.jsx (⋯ menu Send)
//
// Each consumer hands the recommender a {stage, daysUntilExpiration,
// daysSinceLastActivity} bundle pulled off the pipeline-item shape
// (days_until_expiration + days_since_last_activity — already on the
// item from routes/tracon.routes.js buildPipelineItem).

// Static fallback used by recommendedForStage() — for surfaces that
// don't carry the timing context yet, this is the prior-Band-0 map.
export const STAGE_RECOMMENDED_TEMPLATES = {
  'Renewal Pending': ['renewal-invitation'],
  Prospect: ['applicant-confirmation', 'delegation'],
  'Submitted to Market': [],
  Quoted: ['quotes-ready'],
  Presented: ['quotes-ready'],
  Paid: ['coi-delivery'],
  Bound: ['renewal-reminder'],
};

// Backward-compat — Band 0 callers pass just the stage. Keep working.
export function recommendedForStage(stage) {
  return (stage && STAGE_RECOMMENDED_TEMPLATES[stage]) || [];
}

// Primary recommendation surface. Pass the full {stage, daysUntil,
// daysSinceLastActivity} from the pipeline item. Returns an ordered
// slug list — index 0 is PRIMARY (top of picker), additional entries
// are SECONDARY options worth surfacing.
//
// Rules (TRACON Vision §V2 fix #2 2026-05-20):
//
//   Renewal Pending
//     > 14d to expiration         → [renewal-reminder]
//     8–14d                       → [renewal-reminder, non-renewal-notice]
//     ≤ 7d AND quiet ≥7d          → [non-renewal-notice, renewal-reminder]
//     ≤ 7d AND client active <7d  → [renewal-reminder]
//
//   Quoted                        → [quotes-ready]
//
//   Presented
//     quiet ≥5d                   → [quote-request-followup, quotes-ready]
//     active                      → [quotes-ready]
//     (Note: 'quote-request-followup' isn't currently in the V2 tracon-send
//      template registry. Suggestion is recorded for the moment the slug
//      lands; until then the picker resolves it via best-match to whichever
//      followup template is registered — typically 'renewal-reminder' for
//      generic nudge intent. UI gracefully no-ops on a missing slug.)
//
//   Paid                          → [coi-delivery]
//
//   Bound (RENEWAL_DUE pipeline stage)
//     ≤ 100d to expiration        → [renewal-invitation]  (initiation, not reminder)
//
//   default                       → recommendedForStage(stage)
export function recommendedForContext(ctx) {
  const {
    stage,
    daysUntilExpiration = null,
    daysSinceLastActivity = null,
  } = ctx || {};
  if (!stage) return [];
  const quiet = (n) => daysSinceLastActivity != null && daysSinceLastActivity >= n;

  switch (stage) {
    case 'Renewal Pending': {
      const d = daysUntilExpiration;
      if (d == null) return ['renewal-reminder'];
      if (d > 14) return ['renewal-reminder'];
      if (d > 7) return ['renewal-reminder', 'non-renewal-notice'];
      // ≤ 7d.
      if (quiet(7)) return ['non-renewal-notice', 'renewal-reminder'];
      return ['renewal-reminder', 'non-renewal-notice'];
    }
    case 'Quoted':
      return ['quotes-ready'];
    case 'Presented':
      if (quiet(5)) return ['quote-request-followup', 'quotes-ready'];
      return ['quotes-ready'];
    case 'Paid':
      return ['coi-delivery'];
    case 'Bound':
      // RENEWAL_DUE band — pipeline shows these as Bound + days_until
      // ≤ 100. The initiation email is renewal-invitation (one-shot;
      // creates the new Deal + session). Renewal-reminder is for
      // post-initiation Renewal Pending nudges, not for Bound deals.
      if (daysUntilExpiration != null && daysUntilExpiration <= 100) {
        return ['renewal-invitation'];
      }
      return [];
    case 'Prospect':
      return ['applicant-confirmation', 'delegation'];
    default:
      return recommendedForStage(stage);
  }
}
